import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Clapperboard, Mic2, PlayCircle, Sparkles, Video } from 'lucide-react';

const AynaraVideoGallery = () => {
  const [videos, setVideos] = useState([]);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/video-demos`)
      .then((r) => r.json())
      .then((d) => setVideos(d.videos || []))
      .catch(() => {});
  }, []);

  if (!videos.length) return null;
  return (
    <section className="glass-card tracing-panel p-golden-xl" data-testid="media-demos-aynara-gallery">
      <div className="flex items-center gap-3 text-text-heading">
        <Clapperboard className="w-5 h-5 text-primary" />
        <h2 className="text-2xl font-semibold">Vídeos generados por Aynara, con su voz</h2>
      </div>
      <p className="mt-3 max-w-3xl text-text-body">Campañas reales creadas automáticamente por nuestra IA: guion, narración con la voz de Aynara y render final sin intervención humana. Así trabajaría para tu negocio.</p>
      <div className="mt-6 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
        {videos.map((v) => (
          <div key={v.id} className="overflow-hidden rounded-[24px] border border-border-subtle bg-background/55" data-testid={`aynara-gallery-video-${v.id}`}>
            <video controls preload="metadata" poster={v.snapshot_url || undefined} src={v.video_url} className="aspect-[9/16] max-h-[420px] w-full bg-black object-contain" />
            <div className="p-4">
              <p className="text-sm font-semibold text-text-heading">{v.title}</p>
              <p className="mt-1 text-xs text-text-muted">{v.template_name} · Narración IA de Aynara</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

const hooks = [
  'Las empresas no se rompen de golpe. Se rompen por desorden acumulado.',
  'La IA no sustituye a la dirección. La amplifica.',
  'Si tu negocio crece pero todo depende de ti, no has escalado.',
  'Lo que no ordenas, te quita tiempo. Lo que no recuerdas, te quita control.',
  'Más herramientas no significa mejor sistema. A veces significa más caos.',
  'Lo que no alimentas, se degrada: ventas, equipo, operación y marca.',
  'La ventaja real no es automatizar una tarea. Es coordinar todo el sistema.',
  'Si no tienes memoria operativa, repites errores caros una y otra vez.',
  'El futuro no es usar IA. El futuro es dirigirla.',
  'Neuralia369 existe para ganar tiempo al tiempo.',
  'Sin estrategia, hasta la mejor tecnología se dispersa.',
  'Una empresa no crece por más esfuerzo. Crece por mejor estructura.',
];

const shortClips = [
  { duration: '10-25s', structure: 'Hook duro + principio + golpe final', idea: 'Entropía empresarial: lo que no ordenas se rompe.', cta: 'Escríbenos “orden” y te enseñamos la ruta.' },
  { duration: '25-45s', structure: 'Metáfora + aplicación negocio + cierre', idea: 'De la estrategia militar a la estrategia empresarial.', cta: 'Pide diagnóstico y te mostramos qué corregir primero.' },
  { duration: '45-60s', structure: 'Idea + ejemplo + solución Neuralia369', idea: 'Más herramientas no es más control.', cta: 'Descubre cómo unir ventas, operación y memoria.' },
  { duration: '60-90s', structure: 'Marco conceptual + prueba + propuesta', idea: 'Cómo se degrada una empresa sin energía estratégica.', cta: 'Reserva videollamada y diseñamos tu siguiente paso.' },
];

const youtubeIdeas = [
  'Por qué las empresas pierden tiempo sin darse cuenta',
  'La ley de la entropía aplicada al negocio moderno',
  'Cómo coordinar IA, equipos y procesos sin crear más caos',
  'La diferencia entre automatizar tareas y construir un sistema',
  'Por qué la memoria operativa será la mayor ventaja de las empresas',
  'Neuralia369: de herramienta a sistema operativo empresarial',
];

const demoClips = [
  {
    title: 'Demo 01 · Estrategia > habilidad',
    source: 'Video de estrategia',
    timing: '00:35 → 00:52 y 01:03 → 01:12',
    hook: 'El secreto de la victoria según un Samurai legendario...',
    goal: 'Autoridad + visión',
    cta: 'Escríbenos “estrategia” y te mostramos cómo se aplica a tu empresa.',
  },
  {
    title: 'Demo 02 · Velocidad estratégica',
    source: 'Video de estrategia',
    timing: '05:07 → 05:34 y 06:17 → 06:32',
    hook: '¿Cómo ganarle a tu competencia en 40 segundos?',
    goal: 'Conversión + tensión intelectual',
    cta: 'Pide diagnóstico y detectamos dónde tu negocio pierde velocidad.',
  },
  {
    title: 'Demo 03 · Entropía empresarial',
    source: 'Video de entropía',
    timing: '00:22 → 00:30 y 01:16 → 01:25',
    hook: 'La verdad incómoda: nada se mantiene solo.',
    goal: 'Autoridad + captación',
    cta: 'Si quieres recuperar orden y control, agenda una sesión con Neuralia369.',
  },
];

const styleRules = [
  'Usar tu cara real en piezas de autoridad y reflexión, no en todo.',
  'Combinar formato founder-led, clips documentales y piezas conceptuales.',
  'Mantener tono premium, estratégico, claro y humano.',
  'Evitar imitar cadencias exactas; construir una voz propia Neuralia369.',
  'Visuales limpios, editoriales, con texto potente y ritmo firme.',
  'Usar conceptos madre: tiempo, orden, memoria, control, dirección, expansión.',
];

export default function MediaDemosPage() {
  return (
    <>
      <Helmet>
        <title>Media Demos | Neuralia369</title>
      </Helmet>

      <div className="min-h-screen bg-background py-golden-xl">
        <div className="max-w-7xl mx-auto px-golden-md lg:px-golden-lg space-y-8">
          <section className="glass-card tracing-panel p-golden-xl" data-testid="media-demos-hero">
            <p className="editorial-kicker mb-4">Media demos internas</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl text-text-heading">Primera tanda de demos para la comunicación de Neuralia369</h1>
            <p className="mt-5 text-text-body text-lg max-w-3xl">Esta página convierte los vídeos de referencia y la entrevista TV en un sistema de comunicación propio: hooks, estructuras, ideas YouTube y reglas de estilo para que podamos producir contenido con tu cara, con clonación controlada o con formatos híbridos sin parecer copia.</p>
          </section>

          <AynaraVideoGallery />

          <section className="grid gap-6 xl:grid-cols-[1.05fr_0.95fr]">
            <div className="glass-card p-golden-xl" data-testid="media-demos-hooks-card">
              <div className="flex items-center gap-3 text-text-heading"><Mic2 className="w-5 h-5 text-primary" /><h2 className="text-2xl font-semibold">12 hooks listos para clips</h2></div>
              <div className="mt-6 grid gap-3">
                {hooks.map((hook, index) => (
                  <div key={index} className="rounded-[22px] border border-border-subtle bg-background/55 p-4" data-testid={`media-demos-hook-${index}`}>
                    <p className="text-sm text-text-body">{hook}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-6">
              <div className="glass-card p-golden-xl" data-testid="media-demos-style-card">
                <div className="flex items-center gap-3 text-text-heading"><Sparkles className="w-5 h-5 text-primary" /><h2 className="text-2xl font-semibold">Reglas de estilo Neuralia369</h2></div>
                <div className="mt-5 space-y-3">
                  {styleRules.map((rule, index) => (
                    <div key={index} className="rounded-[22px] border border-border-subtle bg-background/55 p-4" data-testid={`media-demos-style-rule-${index}`}>
                      <p className="text-sm text-text-body">{rule}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="glass-card p-golden-xl" data-testid="media-demos-avatar-card">
                <div className="flex items-center gap-3 text-text-heading"><Video className="w-5 h-5 text-primary" /><h2 className="text-2xl font-semibold">Uso de cara / clon / híbrido</h2></div>
                <p className="mt-4 text-text-body">La recomendación para Neuralia369 es un modelo híbrido: piezas con tu cara real para autoridad, piezas con clonación controlada cuando queramos velocidad/escala y piezas conceptuales sin rostro para elevar la marca. Así se siente más premium, más propio y menos “copia”.</p>
              </div>
            </div>
          </section>

          <section className="glass-card p-golden-xl" data-testid="media-demos-short-form-card">
            <div className="flex items-center gap-3 text-text-heading"><PlayCircle className="w-5 h-5 text-primary" /><h2 className="text-2xl font-semibold">4 estructuras para TikTok / Reels / Shorts</h2></div>
            <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              {shortClips.map((clip) => (
                <div key={clip.duration} className="rounded-[24px] border border-border-subtle bg-background/55 p-5" data-testid={`media-demos-clip-${clip.duration}`}>
                  <p className="text-xs uppercase tracking-[0.18em] text-text-muted">{clip.duration}</p>
                  <p className="mt-3 text-lg font-semibold text-text-heading">{clip.idea}</p>
                  <p className="mt-2 text-sm text-text-body">{clip.structure}</p>
                  <p className="mt-3 text-xs text-primary">CTA: {clip.cta}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="glass-card p-golden-xl" data-testid="media-demos-first-batch-card">
            <div className="flex items-center gap-3 text-text-heading"><Clapperboard className="w-5 h-5 text-primary" /><h2 className="text-2xl font-semibold">Primera tanda de 3 demos a producir</h2></div>
            <p className="mt-4 text-text-body">Estas son las 3 primeras piezas que recomiendo producir como demostración visual real. Están elegidas para mezclar autoridad, conversión y diferenciación sin parecer una copia de las referencias.</p>
            <div className="mt-6 grid gap-4 xl:grid-cols-3">
              {demoClips.map((clip, index) => (
                <div key={clip.title} className="rounded-[24px] border border-border-subtle bg-background/55 p-5" data-testid={`media-demos-first-batch-${index}`}>
                  <p className="text-xs uppercase tracking-[0.18em] text-text-muted">{clip.source}</p>
                  <h3 className="mt-3 text-xl font-semibold text-text-heading">{clip.title}</h3>
                  <p className="mt-2 text-sm text-text-body"><strong>Timing:</strong> {clip.timing}</p>
                  <p className="mt-2 text-sm text-text-body"><strong>Hook:</strong> {clip.hook}</p>
                  <p className="mt-2 text-sm text-text-body"><strong>Objetivo:</strong> {clip.goal}</p>
                  <p className="mt-3 text-xs text-primary"><strong>CTA:</strong> {clip.cta}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="glass-card p-golden-xl" data-testid="media-demos-youtube-card">
            <div className="flex items-center gap-3 text-text-heading"><Clapperboard className="w-5 h-5 text-primary" /><h2 className="text-2xl font-semibold">6 ideas para YouTube</h2></div>
            <div className="mt-6 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {youtubeIdeas.map((idea, index) => (
                <div key={index} className="rounded-[22px] border border-border-subtle bg-background/55 p-4" data-testid={`media-demos-youtube-${index}`}>
                  <p className="text-sm text-text-body">{idea}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </>
  );
}